import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ROADMAP_TARGET } from '../data/site.mjs';
import { fetchNewsPosts, type NewsPost } from '../lib/supabase';

const formatHomeDate = (iso: string) => {
    try {
        const d = new Date(iso);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(2);
        return `${mm} / ${dd} / ${yy}`;
    } catch {
        return '';
    }
};

const AvenBoxMini = lazy(() => import('../components/three/AvenBoxMini'));

gsap.registerPlugin(ScrollTrigger, useGSAP);

const HomePage = () => {
    const root = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const miniSlotRef = useRef<HTMLDivElement | null>(null);
    const [mountMini, setMountMini] = useState(false);

    useEffect(() => {
        const el = miniSlotRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setMountMini(true);
                        io.disconnect();
                        break;
                    }
                }
            },
            { rootMargin: '600px' }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        if (!el.src) el.src = '/hero-video.mp4';
                        el.play().catch(() => {});
                    } else {
                        el.pause();
                    }
                }
            },
            { rootMargin: '200px' }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const indexItems = [
        { key: 'i-1', to: '/security', label: 'Security', blurb: 'Privacy by architecture, not by policy.' },
        { key: 'i-2', to: '/technology', label: 'Technology', blurb: 'System architecture that governs routing.' },
        { key: 'i-3', to: '/avenbox', label: 'AvenBox', blurb: 'Portable private AI control.' },
        { key: 'i-4', to: '/avenx', label: 'Project AvenX', blurb: 'Revolutionizing company infrastructure.', status: 'In development' },
    ] as const;

    const truthBlocks = [
        {
            key: 't-1',
            index: '01',
            tag: 'Yours stays yours',
            headline: 'Your ideas, your notes, your private thinking — they stop being training data for someone else.',
        },
        {
            key: 't-2',
            index: '02',
            tag: 'Stop paying for silence',
            headline: 'No monthly AI subscription quietly billing you for work that never needed to leave your desk.',
        },
        {
            key: 't-3',
            index: '03',
            tag: 'Time back',
            headline: 'AI that handles the busywork in the background, so the work that matters actually gets your attention.',
        },
    ] as const;

    const modes = [
        {
            key: 'm-1',
            numeral: 'I',
            name: 'Local execution path',
            italic: 'Default path',
            body: 'Privacy-sensitive work where the user keeps direct control over context and compute.',
        },
        {
            key: 'm-2',
            numeral: 'II',
            name: 'Trusted extension path',
            italic: 'Explicit only',
            body: 'Capability extension under explicit governance when local resources are insufficient.',
        },
        {
            key: 'm-3',
            numeral: 'III',
            name: 'External access path',
            italic: 'Bounded & visible',
            body: 'External model access when requested, never as an invisible default behavior.',
        },
    ] as const;

    const [newsPosts, setNewsPosts] = useState<NewsPost[] | null>(null);

    useEffect(() => {
        let alive = true;
        fetchNewsPosts().then((data) => {
            if (alive) setNewsPosts(data);
        });
        return () => {
            alive = false;
        };
    }, []);

    const newsItems = (newsPosts ?? []).slice(0, 3).map((post) => ({
        key: `news-${post.id}`,
        to: `/news/${post.slug}`,
        date: formatHomeDate(post.published_at),
        tag: post.category === 'publishing' ? 'Paper' : (post.source_name ?? 'Signal'),
        title: post.title,
    }));

    useGSAP(
        () => {
            // hero: stagger word reveal
            const heroWords = gsap.utils.toArray<HTMLElement>('.aeq-hero__word');
            gsap.from(heroWords, {
                y: 80,
                opacity: 0,
                duration: 1.1,
                ease: 'power3.out',
                stagger: 0.12,
                delay: 0.15,
            });
            gsap.from('.aeq-hero__meta, .aeq-hero__ticker, .aeq-hero__foot', {
                y: 24,
                opacity: 0,
                duration: 0.9,
                ease: 'power2.out',
                stagger: 0.08,
                delay: 0.6,
            });
            gsap.from(['.aeq-hero__rail', '.aeq-hero__coord'], {
                opacity: 0,
                duration: 1.2,
                ease: 'power2.out',
                delay: 0.9,
            });

            // marquee ticker inside hero: infinite slow drift
            const tickerInner = document.querySelector<HTMLElement>('.aeq-hero__ticker');
            if (tickerInner) {
                const distance = tickerInner.scrollWidth - tickerInner.clientWidth;
                if (distance > 20) {
                    gsap.to(tickerInner, {
                        scrollLeft: distance,
                        duration: 28,
                        ease: 'none',
                        repeat: -1,
                        yoyo: true,
                    });
                }
            }

            // reveal-on-scroll: index rows
            gsap.utils.toArray<HTMLElement>('[data-reveal-row]').forEach((row) => {
                gsap.from(row, {
                    y: 40,
                    opacity: 0,
                    duration: 0.9,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: row,
                        start: 'top 88%',
                        once: true,
                    },
                });
            });

            // reveal-on-scroll: big display headings
            gsap.utils.toArray<HTMLElement>('[data-reveal-display]').forEach((el) => {
                gsap.from(el, {
                    y: 60,
                    opacity: 0,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: el, start: 'top 82%', once: true },
                });
            });

            // subtle parallax on section numerals
            gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((el) => {
                gsap.to(el, {
                    yPercent: -18,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true,
                    },
                });
            });
        },
        { scope: root },
    );

    return (
        <div className="aeq-home" ref={root}>
            <section id="overview" className="aeq-hero aeq-hero--beige">
                <span className="aeq-hero__rail" aria-hidden="true">
                    Aven / Sovereign Control Layer / 01
                </span>
                <span className="aeq-hero__coord" aria-hidden="true">
                    N 46°03′ · E 14°30′ — Ljubljana
                </span>

                <div className="aeq-shell aeq-hero__shell">
                    <div className="aeq-hero__meta">
                        <span>AvenBox — sovereign AI control layer</span>
                        <span>Roadmap target: {ROADMAP_TARGET}</span>
                    </div>

                    <h1 className="aeq-hero__title">
                        <span className="aeq-hero__word aeq-hero__word--l1">Sovereign</span>
                        <span className="aeq-hero__word aeq-hero__word--l2">
                            <em>Control.</em>
                        </span>
                        <span className="aeq-hero__word aeq-hero__word--l3">
                            For <em>private AI.</em>
                        </span>
                    </h1>

                    <div className="aeq-hero__ticker" aria-hidden="true">
                        <span>Local execution</span>
                        <span>·</span>
                        <span>Explicit routing</span>
                        <span>·</span>
                        <span>Trust boundary</span>
                        <span>·</span>
                        <span>Continuity across surfaces</span>
                        <span>·</span>
                        <span>User authority</span>
                    </div>

                    <div className="aeq-hero__foot">
                        <p className="aeq-hero__lead">
                            AvenBox is built to keep intelligent work under user authority
                            through local execution, explicit routing, and clear trust
                            boundaries across device, app, and workstation.
                        </p>

                        <div className="aeq-hero__cta">
                            <Link to="/contact" className="aeq-btn aeq-btn--primary">
                                Join Waitlist
                            </Link>
                            <Link to="/contact" className="aeq-btn aeq-btn--secondary">
                                Reserve with EUR 1 deposit
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── AvenBox reveal ──────────────────────────────────────────── */}
            <section id="avenbox-reveal" className="ed-reveal ed-reveal--split">
                <div className="ed-reveal__rail" aria-hidden="true">Object / 01</div>
                <div className="ed-reveal__coord" aria-hidden="true">AvenBox — sovereign unit</div>

                <div className="ed-reveal__grid">
                    <div className="ed-reveal__stage">
                        <img
                            src="/images/avenbox-hand.png"
                            alt="AvenBox held in hand"
                            className="ed-reveal__photo"
                            width={1200}
                            height={1500}
                            loading="lazy"
                            decoding="async"
                            data-reveal-display
                        />
                    </div>

                    <div className="ed-reveal__copy">
                        <span className="ed-reveal__eyebrow">The object in question</span>
                        <h2 className="ed-reveal__title" data-reveal-display>
                            AvenBox. <em>In your hands.</em>
                        </h2>
                        <p className="ed-reveal__body">
                            A sovereign compute unit that keeps intelligence, routing, and
                            trust boundaries under direct user authority — not a cloud
                            endpoint wearing hardware.
                        </p>
                        <Link to="/avenbox" className="ed-reveal__link">
                            Read AvenBox →
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── Editorial index (was media-preview) ─────────────────────── */}
            <section id="media-preview" className="aeq-section ed-section">
                <div className="aeq-shell ed-shell">
                    <div className="ed-section__head">
                        <span className="ed-eyebrow">Index — 01 / System map</span>
                        <h2 className="ed-display" data-reveal-display>
                            A short <em>map</em> of what this company builds.
                        </h2>
                    </div>

                    <div className="ed-index">
                        {indexItems.map((item, i) => {
                            const isDev = 'status' in item && item.status;
                            if (isDev) {
                                return (
                                    <div
                                        key={item.key}
                                        className="ed-index__row ed-index__row--dev"
                                        data-reveal-row
                                        aria-disabled="true"
                                    >
                                        <span className="ed-index__num">{String(i + 1).padStart(2, '0')}</span>
                                        <span className="ed-index__label">{item.label}</span>
                                        <span className="ed-index__blurb">{item.blurb}</span>
                                        <span className="ed-index__badge">{item.status}</span>
                                    </div>
                                );
                            }
                            return (
                                <Link key={item.key} to={item.to} className="ed-index__row" data-reveal-row>
                                    <span className="ed-index__num">{String(i + 1).padStart(2, '0')}</span>
                                    <span className="ed-index__label">{item.label}</span>
                                    <span className="ed-index__blurb">{item.blurb}</span>
                                    <span className="ed-index__arrow" aria-hidden>→</span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── AvenBox mini scroll-lock (wow moment) ───────────────────── */}
            <div ref={miniSlotRef} style={{ minHeight: '100vh', background: '#efefec' }}>
                {mountMini && (
                    <Suspense fallback={<div style={{ height: '100vh', background: '#efefec' }} />}>
                        <AvenBoxMini />
                    </Suspense>
                )}
            </div>

            {/* ── Manifesto video ─────────────────────────────────────────── */}
            <section id="manifesto-video" className="ed-video">
                <video
                    ref={videoRef}
                    className="ed-video__media"
                    loop
                    muted
                    playsInline
                    preload="none"
                />
                <div className="ed-video__overlay" aria-hidden="true">
                    <span className="ed-video__text">Adapt</span>
                    <span className="ed-video__text ed-video__text--italic"><em>or</em></span>
                    <span className="ed-video__text">perish.</span>
                </div>
            </section>

            {/* ── Truth strip — three oversized editorial blocks ──────────── */}
            <section id="truth-strip" className="aeq-section ed-section">
                <div className="aeq-shell ed-shell">
                    <span className="ed-eyebrow">Index — 02 / What you gain</span>
                    <div className="ed-truth">
                        {truthBlocks.map((b) => (
                            <article key={b.key} className="ed-truth__block" data-reveal-row>
                                <span className="ed-truth__num" data-parallax>{b.index}</span>
                                <span className="ed-truth__tag">{b.tag}</span>
                                <p className="ed-truth__headline">{b.headline}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Thesis statement ─────────────────────────────────────────── */}
            <section id="what-this-is" className="aeq-section ed-section ed-section--thesis">
                <div className="aeq-shell ed-shell ed-shell--centered">
                    <span className="ed-eyebrow">Index — 03 / Thesis</span>
                    <h2 className="ed-thesis" data-reveal-display>
                        A system layer between <em>user intent</em> and AI execution.
                    </h2>
                    <p className="ed-thesis__body">
                        This is not a hardware novelty page. AvenBox defines and governs
                        execution paths so private work does not drift into external systems by
                        default. It keeps control attached to the user as model capability grows.
                    </p>
                </div>
            </section>

            {/* ── Execution modes — numbered editorial rows ────────────────── */}
            <section id="execution-modes" className="aeq-section ed-section">
                <div className="aeq-shell ed-shell">
                    <div className="ed-section__head">
                        <span className="ed-eyebrow">Index — 04 / Architecture posture</span>
                        <h2 className="ed-display" data-reveal-display>
                            One governing model. <em>Three</em> controlled execution modes.
                        </h2>
                    </div>

                    <div className="ed-modes">
                        {modes.map((m) => (
                            <div key={m.key} className="ed-modes__row" data-reveal-row>
                                <span className="ed-modes__num" data-parallax>{m.numeral}</span>
                                <div className="ed-modes__body">
                                    <h3 className="ed-modes__name">{m.name}</h3>
                                    <p className="ed-modes__italic"><em>{m.italic}</em></p>
                                    <p className="ed-modes__copy">{m.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="ed-links">
                        <Link to="/technology">Read Technology</Link>
                        <Link to="/security">Read Security</Link>
                        <Link to="/avenbox">Read AvenBox</Link>
                    </div>
                </div>
            </section>

            {/* ── News & Insights ─────────────────────────────────────────── */}
            <section id="news-insights" className="aeq-section ed-section">
                <div className="aeq-shell ed-shell">
                    <div className="ed-news__head">
                        <span className="ed-eyebrow">Index — 05 / Public direction</span>
                        <div className="ed-news__head-row">
                            <h2 className="ed-display" data-reveal-display>News &amp; <em>insights</em></h2>
                            <Link to="/news" className="ed-news__all">All articles →</Link>
                        </div>
                    </div>
                    <div className="ed-news">
                        {newsItems.length === 0 && newsPosts === null && (
                            <p className="news-empty">Loading…</p>
                        )}
                        {newsItems.map((item) => (
                            <Link key={item.key} to={item.to} className="ed-news__card" data-reveal-row>
                                <div className="ed-news__meta">
                                    <span>{item.tag}</span>
                                    <span>{item.date}</span>
                                </div>
                                <h3 className="ed-news__title">{item.title}</h3>
                                <span className="ed-news__read">Read more →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Closing CTA ─────────────────────────────────────────────── */}
            <section id="controlled-access" className="aeq-section ed-section ed-section--close">
                <div className="aeq-shell ed-shell">
                    <span className="ed-eyebrow">Index — 06 / Controlled access</span>
                    <h2 className="ed-close__title" data-reveal-display>
                        Built for users who treat AI as <em>critical infrastructure.</em>
                    </h2>
                    <p className="ed-close__body">
                        Reservation is open through a controlled EUR 1 deposit path. Current
                        public pricing and timing are directional and aligned to {ROADMAP_TARGET}.
                    </p>
                    <div className="ed-close__cta">
                        <Link to="/contact" className="aeq-btn aeq-btn--primary">
                            Join Waitlist
                        </Link>
                        <Link to="/pricing" className="aeq-btn aeq-btn--secondary">
                            Review Pricing
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
